// ════════════════════════════════════════════════════════════════
// Hook: useFeatureRules — loads feature_rules from DB
// ════════════════════════════════════════════════════════════════

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface FeatureRuleRow {
  feature_key: string;
  is_enabled: boolean;
  access_mode: 'open' | 'auth_required' | 'role_restricted' | 'beta_only' | 'disabled';
  requires_auth: boolean;
  requires_verification: boolean;
  min_verification: string | null;
  allowed_roles: string[];
  allowed_beta_program_ids: string[];
  required_beta_program_ids: string[];
}

export function useFeatureRules() {
  const { user } = useAuth();

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['feature-rules'],
    enabled: !!user,
    staleTime: 120_000, // 2 minutes — rules change rarely
    queryFn: async () => {
      // Reads go through a SECURITY DEFINER RPC that returns only the
      // fields the client-side access engine needs. The raw
      // `feature_rules` table is admin-only to avoid exposing internal
      // descriptions / metadata to all signed-in users.
      const { data, error } = await supabase.rpc('get_client_feature_rules');
      if (error) throw error;
      return (data ?? []) as FeatureRuleRow[];
    },
  });

  const rulesMap = new Map(rules.map(r => [r.feature_key, r]));

  const getFeatureRule = (key: string): FeatureRuleRow | undefined =>
    rulesMap.get(key);

  const isFeatureEnabled = (key: string): boolean => {
    const rule = rulesMap.get(key);
    return rule ? rule.is_enabled : true; // default: enabled if no rule
  };

  return { rules, rulesMap, getFeatureRule, isFeatureEnabled, isLoading };
}
